import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ReactElement } from 'react';
import { useQuery } from '@tanstack/react-query';
import { WidgetHeader } from '../WidgetHeader';
import { qfSubscribe } from '../../services/qfClient';
import {
  fetchZerodayBreadth,
  INTERNALS_CHANNEL,
  mergeLivePoint,
  parseInternalsTick,
  type BreadthPoint,
  type InternalsTick,
} from '../../services/zerodayBreadthService';
import { loadPopout, openPopout } from '../../utils/popout';
import { BreadthPanel } from './BreadthPanel';

const SESSIONS = 2;
// The server caches 15s. VIX and VVIX have no live tail, so this refreshes them.
const POLL_MS = 15_000;
// Live minutes kept between polls. A poll supersedes them; this bounds growth
// if REST stalls while the socket keeps publishing.
const MAX_LIVE_MINUTES = 30;
const VIX_COLOR = '#C2A14E';
const VVIX_COLOR = '#1C76A2';
const EMPTY: readonly BreadthPoint[] = [];
const POPOUT_TOOL = 'zeroday-breadth';
const headerButton = 'text-gray-400 hover:text-white text-sm px-2 py-0.5 rounded transition-colors';
const ADD_TITLE = "ADD / ADDQ are QuantForge's house count from lit-venue prints vs the last regular-hours print, so levels run below the exchange-published $ADD; the shape tracks it.";

interface ZerodayBreadthWidgetProps {
  windowId: string;
  onRemove: () => void;
}

interface ZerodayBreadthViewProps {
  onRemove: () => void;
  /** Docked only. Opens a pop-out that is its own window. */
  onPopOut?: () => void;
}

function PopOutIcon(): ReactElement {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.3" aria-hidden="true">
      <path d="M7 1.5h3.5V5M10.5 1.5 5.5 6.5M9 7.5v3H1.5V3h3" />
    </svg>
  );
}

function CloseButton({ onRemove, title }: { onRemove: () => void; title: string }): ReactElement {
  return (
    <button
      type="button"
      onMouseDown={(event) => event.stopPropagation()}
      onClick={onRemove}
      className={`${headerButton} hover:bg-bear-red/20`}
      title={title}
    >
      x
    </button>
  );
}

type VolKey = 'vix' | 'vvix';

interface VolSelection {
  vix: boolean;
  vvix: boolean;
}

/** One of the two vol lines stays on. Turning the last one off is a no-op. */
function toggleVol(current: VolSelection, key: VolKey): VolSelection {
  const next = { ...current, [key]: !current[key] };
  if (!next.vix && !next.vvix) return current;
  return next;
}

function VolToggles({ selection, onToggle }: { selection: VolSelection; onToggle: (key: VolKey) => void }): ReactElement {
  const button = (key: VolKey, label: string): ReactElement => {
    const on = selection[key];
    return (
      <button
        type="button"
        aria-pressed={on}
        onClick={() => onToggle(key)}
        className={`px-1.5 py-0 text-[11px] font-bold tracking-wide transition-colors ${
          on ? 'bg-accent-yellow text-hot-tag-text' : 'text-gray-400 hover:text-white'
        }`}
      >
        {label}
      </button>
    );
  };
  return (
    <div className="flex items-center rounded border border-dark-border overflow-hidden shrink-0">
      {button('vix', 'VIX')}
      {button('vvix', 'VVIX')}
    </div>
  );
}

function withLive(points: readonly BreadthPoint[], ticks: readonly InternalsTick[], key: 'add' | 'addq'): readonly BreadthPoint[] {
  let out = points;
  for (const tick of ticks) out = mergeLivePoint(out, tick.minute, tick[key]);
  return out;
}

/** MAG7, VIX, ADDQ and ADD. The title bar matches the Candle Chart. */
function ZerodayBreadthView({ onRemove, onPopOut }: ZerodayBreadthViewProps): ReactElement {
  const inPopout = onPopOut === undefined;
  const { data, isError } = useQuery({
    queryKey: ['zeroday-breadth', SESSIONS],
    queryFn: () => fetchZerodayBreadth(SESSIONS),
    refetchInterval: POLL_MS,
    staleTime: POLL_MS,
  });

  // Last snapshot per minute; the newest minute keeps being overwritten.
  const [ticks, setTicks] = useState<InternalsTick[]>([]);
  useEffect(() => {
    return qfSubscribe(INTERNALS_CHANNEL, (raw) => {
      const tick = parseInternalsTick(raw);
      if (!tick) return;
      setTicks((prev) => {
        const last = prev[prev.length - 1];
        if (last && tick.minute < last.minute) return prev;
        const kept = last && last.minute === tick.minute ? prev.slice(0, -1) : prev;
        return [...kept, tick].slice(-MAX_LIVE_MINUTES);
      });
    });
  }, []);

  const add = useMemo(() => withLive(data?.add ?? EMPTY, ticks, 'add'), [data, ticks]);
  const addq = useMemo(() => withLive(data?.addq ?? EMPTY, ticks, 'addq'), [data, ticks]);
  const [volOn, setVolOn] = useState<VolSelection>({ vix: true, vvix: false });
  const onVolToggle = useCallback((key: VolKey): void => {
    setVolOn((current) => toggleVol(current, key));
  }, []);
  const vixPoints = data?.vix ?? EMPTY;
  const vvixPoints = data?.vvix ?? EMPTY;
  const volSeries = [
    volOn.vix ? { points: vixPoints, color: VIX_COLOR } : null,
    volOn.vvix ? { points: vvixPoints, color: VVIX_COLOR } : null,
  ].filter((series): series is { points: readonly BreadthPoint[]; color: string } => series !== null);
  const volWithData = volSeries.filter((series) => series.points.length > 0);
  const volPrimary = volWithData[0] ?? volSeries[0] ?? { points: EMPTY, color: VIX_COLOR };
  const volOverlay = volWithData.length > 1 ? volWithData[1] : undefined;
  const volLabel = [volOn.vix ? 'VIX' : null, volOn.vvix ? 'VVIX' : null].filter(Boolean).join(' · ');
  const status = `1m · last ${SESSIONS} sessions${isError ? ' · update failed, retrying' : ''}`;

  return (
    <div className="h-full flex flex-col bg-dark-surface rounded-lg overflow-hidden">
      <WidgetHeader title="Zeroday Breadth" badge="new">
        <span className="text-[11px] text-gray-500 truncate max-w-[14rem]" title={ADD_TITLE}>
          {status}
        </span>
        {!inPopout && (
          <button
            type="button"
            onMouseDown={(event) => event.stopPropagation()}
            onClick={onPopOut}
            className={`${headerButton} hover:bg-dark-surface-light inline-flex items-center`}
            title="Open in a new window"
            aria-label="Open in a new window"
          >
            <PopOutIcon />
          </button>
        )}
        <CloseButton onRemove={onRemove} title={inPopout ? 'Close window' : 'Close widget'} />
      </WidgetHeader>
      <div className="@container flex-1 min-h-0 p-1">
        <div className="grid h-full gap-1 grid-cols-2 grid-rows-2 @3xl:grid-cols-4 @3xl:grid-rows-1">
          <BreadthPanel label="MAG7" points={data?.fatmann ?? EMPTY} histogram decimals={0} />
          <BreadthPanel
            label={volLabel}
            points={volPrimary.points}
            color={volPrimary.color}
            overlayPoints={volOverlay?.points}
            overlayColor={volOverlay?.color}
            controls={<VolToggles selection={volOn} onToggle={onVolToggle} />}
            decimals={2}
          />
          <BreadthPanel label="ADDQ" points={addq} zeroCentered decimals={0} />
          <BreadthPanel label="ADD" points={add} zeroCentered decimals={0} />
        </div>
      </div>
    </div>
  );
}

/** The breadth row on a dashboard. */
export function ZerodayBreadthWidget({ windowId, onRemove }: ZerodayBreadthWidgetProps): ReactElement {
  const onPopOut = useCallback((): void => {
    openPopout(POPOUT_TOOL, windowId, { id: windowId, type: 'zeroday-breadth' });
  }, [windowId]);
  return <ZerodayBreadthView onRemove={onRemove} onPopOut={onPopOut} />;
}

/**
 * The same row in its own window. It has no settings of its own; loading the
 * hand-off still records the window so a reload stays on this route's copy.
 */
export function ZerodayBreadthPopout({ popoutId }: { popoutId: string }): ReactElement {
  useEffect(() => {
    loadPopout(popoutId).catch((error: unknown) => {
      console.error('Failed to load pop-out breadth:', error);
    });
  }, [popoutId]);
  return <ZerodayBreadthView onRemove={() => window.close()} />;
}
